import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";
import { Globe, Users, MapPin, Eye, RefreshCw, Building2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatDistanceToNow } from "date-fns";

function Kpi({ label, value, icon: Icon, accent, sub }) {
  return (
    <Card
      data-testid={`analytics-kpi-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className={`p-5 border-slate-200 shadow-sm ${accent ? "bg-[#0033A0] text-white border-transparent" : ""}`}
    >
      <div className={`flex items-center justify-between ${accent ? "text-white/70" : "text-slate-500"}`}>
        <span className="text-[11px] uppercase tracking-widest font-semibold">{label}</span>
        <Icon size={14} />
      </div>
      <div className={`font-display font-black text-3xl mt-1 ${accent ? "text-white" : "text-slate-900"}`}>{value}</div>
      {sub && <div className={`text-xs mt-1 ${accent ? "text-white/70" : "text-slate-500"}`}>{sub}</div>}
    </Card>
  );
}

function flagEmoji(cc) {
  if (!cc || cc.length !== 2 || cc === "??") return "🌐";
  const A = 0x1f1e6;
  return String.fromCodePoint(A + (cc.charCodeAt(0) - 65)) + String.fromCodePoint(A + (cc.charCodeAt(1) - 65));
}

function relTime(ts) {
  if (!ts) return "";
  try {
    return formatDistanceToNow(new Date(ts), { addSuffix: true });
  } catch {
    return ts;
  }
}

function browserFromUA(ua = "") {
  if (/Edg\//i.test(ua)) return "Edge";
  if (/OPR|Opera/i.test(ua)) return "Opera";
  if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return "Chrome";
  if (/Firefox\//i.test(ua)) return "Firefox";
  if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) return "Safari";
  return "Other";
}

function osFromUA(ua = "") {
  if (/Windows NT/i.test(ua)) return "Windows";
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/iPhone|iPad|iOS/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Linux/i.test(ua)) return "Linux";
  return "—";
}

export default function AdminVisits() {
  const { brand } = useBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      setRefreshing(true);
      const d = await endpoints.analyticsVisits(200);
      setData(d);
    } catch (e) {
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const trendMax = useMemo(() => {
    if (!data?.trend_30d) return 0;
    return Math.max(...data.trend_30d.map((d) => d.count), 0);
  }, [data]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-8" data-testid="admin-visits-page">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (<Skeleton key={i} className="h-24" />))}
          </div>
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  const k = data?.kpis || {};
  const top = data?.top_countries || [];
  const cities = data?.top_cities || [];
  const trend = data?.trend_30d || [];
  const recent = data?.recent || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-visits-page">
      {/* Header stripe */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-[#0033A0] grid place-items-center">
              <Globe size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{brand.small} · Flexspace</div>
              <div className="font-display font-bold text-slate-900 text-lg leading-tight">Site Analytics · Visits by Country</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">Admin view · unlinked</Badge>
            <Button
              data-testid="analytics-refresh-btn"
              variant="outline"
              size="sm"
              onClick={load}
              disabled={refreshing}
              className="border-slate-300"
            >
              <RefreshCw size={13} className={`mr-1.5 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 py-8 space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Total Visits" value={k.total_visits?.toLocaleString() || 0} icon={Eye} accent sub="Since first visit tracked" />
          <Kpi label="Unique Visitors" value={k.unique_visitors?.toLocaleString() || 0} icon={Users} sub="Distinct IPs" />
          <Kpi label="Countries" value={k.countries || 0} icon={Globe} sub="With ≥ 1 known visit" />
          <Kpi label="Cities" value={k.cities || 0} icon={MapPin} sub="Distinct locations" />
        </div>

        {/* Trend line */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <TrendIcon /> Visits · last 30 days
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Daily traffic trend</h2>
          <div className="h-56 mt-4" data-testid="analytics-trend-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trend} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 10, fill: "#64748b" }}
                  tickFormatter={(v) => v.slice(5)}
                  interval={4}
                />
                <YAxis tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} domain={[0, Math.max(4, trendMax)]} />
                <Tooltip
                  contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }}
                  labelStyle={{ color: "#0f172a", fontWeight: 600 }}
                />
                <Line type="monotone" dataKey="count" stroke="#0033A0" strokeWidth={2.5} dot={{ r: 3, fill: "#0033A0" }} activeDot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Countries + Cities */}
        <div className="grid lg:grid-cols-2 gap-6">
          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Globe size={12} className="text-[#0033A0]" /> Top countries
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Where visitors are coming from</h2>
            {top.length === 0 ? (
              <div className="mt-8 text-sm text-slate-500 text-center py-10">No visits yet. Share the site URL and refresh.</div>
            ) : (
              <div className="h-72 mt-4" data-testid="analytics-country-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={top.slice(0, 10)} layout="vertical" margin={{ top: 4, right: 24, left: 4, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 10, fill: "#64748b" }} allowDecimals={false} />
                    <YAxis
                      type="category"
                      dataKey="country"
                      tick={{ fontSize: 11, fill: "#0f172a" }}
                      width={120}
                    />
                    <Tooltip contentStyle={{ border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12 }} />
                    <Bar dataKey="count" fill="#0033A0" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>

          <Card className="p-6 border-slate-200 shadow-sm">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
              <Building2 size={12} className="text-[#0033A0]" /> Top cities
            </div>
            <h2 className="font-display font-bold text-xl text-slate-900 mt-1">City-level detail</h2>
            <div className="mt-4 border border-slate-200 rounded-md overflow-hidden">
              <table className="w-full text-sm" data-testid="analytics-city-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold">City</th>
                    <th className="text-left px-4 py-2 font-semibold">Country</th>
                    <th className="text-right px-4 py-2 font-semibold">Visits</th>
                  </tr>
                </thead>
                <tbody>
                  {cities.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-500 text-xs">No data yet.</td></tr>
                  ) : cities.map((c, i) => (
                    <tr key={`${c.city}-${c.country}-${i}`} className="border-t border-slate-200 hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-900">{c.city || "—"}</td>
                      <td className="px-4 py-2 text-slate-700 text-xs">{flagEmoji(c.country_code)} {c.country}</td>
                      <td className="px-4 py-2 text-right font-mono text-slate-900">{c.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>

        {/* Recent visits */}
        <Card className="p-6 border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
            <Eye size={12} className="text-[#0033A0]" /> Recent visits · last {recent.length}
          </div>
          <h2 className="font-display font-bold text-xl text-slate-900 mt-1">Access log</h2>
          <p className="text-xs text-slate-500 mt-1">
            IPs are partially masked for privacy. Location, browser and OS derived from the request headers + <span className="font-mono">ip-api.com</span> geolocation (cached 30d).
          </p>
          <div className="mt-4 border border-slate-200 rounded-md overflow-hidden overflow-x-auto">
            <table className="w-full text-sm" data-testid="analytics-recent-table">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-semibold">When</th>
                  <th className="text-left px-4 py-2 font-semibold">Location</th>
                  <th className="text-left px-4 py-2 font-semibold">IP (masked)</th>
                  <th className="text-left px-4 py-2 font-semibold">ISP</th>
                  <th className="text-left px-4 py-2 font-semibold">Path</th>
                  <th className="text-left px-4 py-2 font-semibold">Persona</th>
                  <th className="text-left px-4 py-2 font-semibold">Device</th>
                </tr>
              </thead>
              <tbody>
                {recent.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500 text-xs">No visits yet.</td></tr>
                ) : recent.map((r) => (
                  <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-600 text-xs whitespace-nowrap">{relTime(r.timestamp)}</td>
                    <td className="px-4 py-2 text-slate-900 text-xs whitespace-nowrap">
                      <span className="mr-1.5">{flagEmoji(r.country_code)}</span>
                      {r.city ? `${r.city}, ` : ""}<span className="text-slate-500">{r.country}</span>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.ip_masked}</td>
                    <td className="px-4 py-2 text-xs text-slate-600 truncate max-w-[180px]">{r.isp || "—"}</td>
                    <td className="px-4 py-2 font-mono text-xs text-slate-700">{r.path}</td>
                    <td className="px-4 py-2 text-xs">
                      {r.persona ? (
                        <Badge className="bg-slate-100 text-slate-700 hover:bg-slate-100 text-[10px] capitalize">{r.persona}</Badge>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">
                      {browserFromUA(r.user_agent)} · <span className="text-slate-400">{osFromUA(r.user_agent)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        <div className="text-[10px] text-slate-400 text-center pb-6">
          {brand.footer} · Flexspace internal analytics · This page is unlinked from the main navigation.
        </div>
      </div>
    </div>
  );
}

function TrendIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#0033A0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 17 9 11 13 15 21 7" />
      <polyline points="14 7 21 7 21 14" />
    </svg>
  );
}
